import React, { useRef, useEffect } from 'react';

export const Waveform = ({ buffer, currentTime, duration, color = '#D0F224', onSeek }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        if (!buffer || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const container = containerRef.current;

        const dpr = window.devicePixelRatio || 1;
        const rect = container.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);
        canvas.style.width = `${rect.width}px`;
        canvas.style.height = `${rect.height}px`;

        const channelData = buffer.getChannelData(0);
        const width = rect.width;
        const height = rect.height;
        const step = Math.ceil(channelData.length / width);

        // Clear canvas
        ctx.fillStyle = '#0A0A0A';
        ctx.fillRect(0, 0, width, height);

        // Draw center line
        ctx.strokeStyle = '#1E1E1E';
        ctx.beginPath();
        ctx.moveTo(0, height / 2);
        ctx.lineTo(width, height / 2);
        ctx.stroke();

        const playProgress = duration > 0 ? currentTime / duration : 0;
        const playX = width * playProgress;

        // Draw waveform
        for (let i = 0; i < width; i++) {
            let min = 1.0;
            let max = -1.0;

            for (let j = 0; j < step; j++) {
                const index = (i * step) + j;
                if (index < channelData.length) {
                    const datum = channelData[index];
                    if (datum < min) min = datum;
                    if (datum > max) max = datum;
                }
            }

            const barHeight = Math.max(2, ((max - min) / 2) * height * 0.8);
            const y = (height - barHeight) / 2;

            // Color based on playback position
            if (i < playX) {
                ctx.fillStyle = color;
            } else {
                ctx.fillStyle = `${color}40`;
            }
            ctx.fillRect(i, y, 1, barHeight);
        }

        // Draw playhead
        if (playX > 0) {
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(playX - 1, 0, 2, height);
        }

    }, [buffer, currentTime, duration, color]);

    const handleClick = (e) => {
        if (!duration || !containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const progress = x / rect.width;
        const newTime = progress * duration;
        onSeek?.(newTime);
    };

    return (
        <div 
            ref={containerRef}
            className="waveform-display cursor-pointer"
            onClick={handleClick}
            data-testid="waveform"
        >
            <canvas ref={canvasRef} className="w-full h-full" />
        </div>
    );
};

export const SpectrumAnalyzer = ({ analyser, color = '#00F0FF' }) => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);

    useEffect(() => {
        if (!analyser || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const draw = () => {
            animationRef.current = requestAnimationFrame(draw);
            
            analyser.getByteFrequencyData(dataArray);
            
            const width = canvas.width;
            const height = canvas.height;
            
            ctx.fillStyle = '#0A0A0A';
            ctx.fillRect(0, 0, width, height);
            
            const barCount = 32;
            const barWidth = (width / barCount) - 2;
            const step = Math.floor(bufferLength / barCount);
            
            for (let i = 0; i < barCount; i++) {
                const value = dataArray[i * step];
                const barHeight = (value / 255) * height * 0.9;
                const x = i * (barWidth + 2);
                const y = height - barHeight;
                
                // Gradient effect
                const gradient = ctx.createLinearGradient(x, height, x, y);
                gradient.addColorStop(0, color);
                gradient.addColorStop(1, `${color}40`);
                
                ctx.fillStyle = gradient;
                ctx.fillRect(x, y, barWidth, barHeight);
            }
        };

        draw();

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [analyser, color]);

    return (
        <canvas 
            ref={canvasRef} 
            width={300} 
            height={80}
            className="w-full h-20 rounded-lg"
            data-testid="spectrum-analyzer"
        />
    );
};

export const LevelMeter = ({ level = -60, peak = -60 }) => {
    const normalizedLevel = Math.max(0, Math.min(100, ((level + 60) / 60) * 100));
    const normalizedPeak = Math.max(0, Math.min(100, ((peak + 60) / 60) * 100));

    return (
        <div className="h-32 w-6 bg-[#1E1E1E] rounded relative overflow-hidden" data-testid="level-meter">
            {/* Level fill */}
            <div 
                className="absolute bottom-0 left-0 right-0 meter-gradient transition-all duration-75"
                style={{ height: `${normalizedLevel}%` }}
            />
            {/* Peak indicator */}
            <div 
                className="absolute left-0 right-0 h-1 bg-white transition-all duration-150"
                style={{ bottom: `${normalizedPeak}%` }}
            />
            {/* Scale markers */}
            {[-6, -12, -24, -48].map(db => (
                <div 
                    key={db}
                    className="absolute left-0 right-0 h-px bg-white/20"
                    style={{ bottom: `${((db + 60) / 60) * 100}%` }}
                />
            ))}
        </div>
    );
};
